import React, { FunctionComponent, useEffect, useState } from 'react';
import { useTheme } from '@mui/material/styles';
// import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
// import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next'
// import useMediaQuery from '@mui/material/useMediaQuery';

// import { check_subdomain } from '../../lib/server_helper';
// import { fetch_all, fetch_all_with_count, fetch_one } from "../../../../lib/v31lib";
import { loginAtom } from '../../../../lib/auth';
// import { currentPracticeAtom } from '../../../../lib/practice';

import ExpandMore from '@mui/icons-material/ExpandMore';
import ExpandLess from '@mui/icons-material/ExpandLess';

import {
  Divider,
  Grid,
  Paper,
} from '@mui/material';

import {
  Button,
  Typography
} from '../../../../components/v2/styled';

import { User } from '../../../../models/User';
import { ConsentLogs } from '../../../../components/v2/dialogs/ConsentLogs';
import HelpersClientWidgets from '../../../../actions/helpers/client_widgets';

// const { DateTime } = require("luxon");

type Props = {
  object: User;
}

// Per-consent runtime render config: the i18n label key + the value reader off
// the viewed client's consent_status. Kept here (UI concern) rather than in
// the helper, which only owns data + pure logic — same split as Actions'
// ACTION_RENDER. The sub-consents carry a !is_secretary runtime gate (the
// enabled master consent always shows); enabled + is_blocked have no gate.
//
// Each consent renders as a yes/pending/no row (consentColour). is_blocked is
// NOT a consent item — it's a fixed conditional header shown above the pinned
// `enabled` item when the client is blocked.
type ConsentRender = {
  labelKey: string;
  gate: (login: any) => boolean;
  value: (object: User) => boolean;
};

const CONSENT_RENDER: Record<string, ConsentRender> = {
  enabled: {
    labelKey: 'coach.consents.enabled',
    gate: () => true,
    value: (o) => !!o.consent_status?.enabled,
  },
  is_intake: {
    labelKey: 'coach.consents.is_intake',
    gate: (login) => !login?.is_secretary,
    value: (o) => !!o.consent_status?.is_intake,
  },
  is_measure_weight: {
    labelKey: 'coach.consents.is_measure_weight',
    gate: (login) => !login?.is_secretary,
    value: (o) => !!o.consent_status?.is_measure_weight,
  },
  is_blood: {
    labelKey: 'coach.consents.is_blood',
    gate: (login) => !login?.is_secretary,
    value: (o) => !!o.consent_status?.is_blood,
  },
  is_diary_eat: {
    labelKey: 'coach.consents.is_diary_eat',
    gate: (login) => !login?.is_secretary,
    value: (o) => !!o.consent_status?.is_diary_eat,
  },
  is_diary_move: {
    labelKey: 'coach.consents.is_diary_move',
    gate: (login) => !login?.is_secretary,
    value: (o) => !!o.consent_status?.is_diary_move,
  },
  is_daynote: {
    labelKey: 'coach.consents.is_daynote',
    gate: (login) => !login?.is_secretary,
    value: (o) => !!o.consent_status?.is_daynote,
  },
};

export const CoachUserWidgetsConsentInformation: FunctionComponent<Props> = ({
  object
}) => {
  const [login] = useAtom(loginAtom);
  const theme = useTheme();
  const {t, i18n} = useTranslation(['translations']);

  const [showAll, setShowAll] = useState<boolean>(false);
  const [activeConsentLogs, setActiveConsentLogs] = useState<number|undefined>(undefined);

  // Consent settings are a coach preference, stored alongside the widget layout
  // on login.settings.userdetails_widgets.settings. `consent_information` is a
  // composite value bundling { items, show_logs } under one key (like
  // details_contact's { fields, hide_empty, edit }):
  // - `items`     : per-consent show/hide map (shown -> directly visible,
  //                 hidden -> behind a "show all" toggle)
  // - `show_logs`  : widget-level toggle for the "goto consent logs" footer
  //                 (default on, preserves historical always-shown behaviour)
  // `consent_information_order` (optional) overrides the render order of the
  // reorderable sub-consents (the pinned `enabled` always leads and is
  // excluded from the order key).
  const widgetSettings = login?.settings?.userdetail_widgets?.settings;
  const cfg = widgetSettings?.[HelpersClientWidgets.consentsValueKey] || {};
  const visibility = cfg.items || {};
  const showLogs = cfg.show_logs !== false; // default on
  const order = widgetSettings?.[HelpersClientWidgets.consentsOrderKey];

  // Ordered + state-tagged items from the coach's preference (pinned `enabled`
  // first), then filtered by each consent's runtime gate (per-client state +
  // secretary check). A single ordered list is rendered: shown items always
  // appear; hidden items are woven into their saved-order position and toggled
  // on/off by the "show all" bool (still reachable — some sub-consents may be
  // tucked but still worth surfacing on demand).
  const ordered = HelpersClientWidgets.getOrderedConsents(order, visibility);
  const renderable = ordered.filter((item) => {
    const cfg = CONSENT_RENDER[item.key];
    return !!cfg && cfg.gate(login);
  });
  const hasHidden = renderable.some((i) => i.state === 'hidden');

  // Footer gate: the consent-logs CTA is fixed chrome, shown when the
  // coach's show_logs toggle is on AND the client has a consent_status.id.
  const canShowLogs = showLogs && !!object.consent_status?.id;

  let consentColour = (bool:boolean, pending: boolean) => {
    return <>
      {bool && <Typography sx={{
        color: theme.palette.primary.main
      }}>{t("coach.consents.option_yes")}</Typography>}
      {!bool && pending && <Typography sx={{
        color: theme.palette.warning.main
      }}>{t("coach.consents.pending")}</Typography>}
      {!bool && !pending && <Typography sx={{
        color: theme.palette.secondary.main
      }}>{t("coach.consents.option_no")}</Typography>}
    </>
  }

  const renderRow = (item: any) => {
    const cfg = CONSENT_RENDER[item.key];
    if (!cfg) return null;
    const value = cfg.value(object);
    return <Grid key={item.key} direction="column" item xs={12}>
      <Typography variant="subtitle2" html>{t(cfg.labelKey)}</Typography>
      {consentColour(value, !!object.consent_status?.pending)}
    </Grid>;
  };

  return <Paper sx={{padding: 2, marginTop: 1, position: 'relative'}}>
    {/* Logs CTA: small text button top-right, absolute in the title row
        (space-saving — costs no extra vertical space). Same ConsentLogs
        dialog target as before. */}
    {canShowLogs && <Button
      label={t("coach.general.actions.goto_consent_logs")}
      id="goto_consent_logs"
      text
      size="small"
      startIcon={<HelpersClientWidgets.consentsShowLogsIcon sx={{fontSize: '1.1rem'}} />}
      sx={{position: 'absolute', top: 4, right: 4, marginTop: 0, zIndex: 2}}
      onClick={() => {
        setActiveConsentLogs(object.consent_status?.id);
      }}
    />}

    <Grid container spacing={1}>
      <Grid item xs={12}>
        <Typography variant="h5" html>{t("coach.submenu.detail.consent_information")}</Typography>
        <Divider />
      </Grid>

      {/* Fixed conditional blocked header — not a consent item, always on top
          when the client is blocked (above the pinned `enabled` row). */}
      {!!object.consent_status?.is_blocked && <Grid direction="column" item xs={12}>
        <Typography variant="subtitle2" html sx={{color: theme.palette.secondary.main, fontWeight: 'bold', fontSize: '1.2rem'}}>{t("coach.consents.is_blocked")}</Typography>
      </Grid>}

      {renderable.map((item) =>
        item.state === 'shown' || showAll ? renderRow(item) : null
      )}

      {hasHidden && <Grid item xs={12} sx={{textAlign: 'center', marginTop: 1}}>
        <Button
          label={showAll
            ? t("coach.personal_settings.userdetail_widgets.consent_information.show_less")
            : t("coach.personal_settings.userdetail_widgets.consent_information.show_all")}
          id="consents_show_all"
          text
          sx={{marginTop: 0}}
          endIcon={showAll ? <ExpandLess /> : <ExpandMore />}
          onClick={() => setShowAll(!showAll)}
        />
      </Grid>}
    </Grid>

    {!!activeConsentLogs && <ConsentLogs
      open={!!activeConsentLogs}
      setOpen={(b) => {
        setActiveConsentLogs(undefined);
      }}
      consentId={activeConsentLogs}
      isCoach
    />}
  </Paper>;
}
